/**
 * 미션 제출 페이지 — 캠페인 정보 + 계정 연동 + 쿠폰/주문 상태 섹션
 *
 * 캠페인 상품 정보를 왼쪽에, 주문/배송 안내를 오른쪽에 배치한다.
 * 배송 방식(cafe24_order / self_delivery)에 따라 UI가 달라진다.
 */

import { useState } from 'react'
import type {
  PublicCampaignResponse,
  MyApplicationResponse,
  OrderStatusResponse,
} from '@/lib/seedingPublicApi'
import { seedingPublicApi } from '@/lib/seedingPublicApi'
import { getCourierLabel, getTrackingUrl } from '@/lib/couriers'
import { formatDate } from '@/features/apply/components/utils'

interface CampaignInfoSectionProps {
  campaign: PublicCampaignResponse
  application: MyApplicationResponse
  mallId: string
  shopNo: number
  campaignId: string
  previewMode: boolean
  forceMobile: boolean
  orderStatus: OrderStatusResponse | null
  orderStatusLoading: boolean
  onApplicationUpdate: (app: MyApplicationResponse) => void
}

export function CampaignInfoSection({
  campaign,
  application,
  mallId,
  shopNo,
  campaignId,
  previewMode,
  forceMobile,
  orderStatus,
  orderStatusLoading,
  onApplicationUpdate,
}: CampaignInfoSectionProps) {
  // Cafe24 회원 검증 상태
  const [cafe24MemberId, setCafe24MemberId] = useState('')
  const [isVerifyingMember, setIsVerifyingMember] = useState(false)
  const [memberVerifyError, setMemberVerifyError] = useState<string | null>(null)
  const [nameMismatchWarning, setNameMismatchWarning] = useState<{
    customer_name: string
    applicant_name: string
  } | null>(null)

  // 쿠폰 요청 관련 상태
  const [selectedCouponVariant, setSelectedCouponVariant] = useState<string | null>(null)
  const [isRequestingCoupon, setIsRequestingCoupon] = useState(false)
  const [couponRequestError, setCouponRequestError] = useState<string | null>(null)

  // Cafe24 회원 ID 검증
  const handleVerifyCafe24Member = async (force: boolean = false) => {
    if (!cafe24MemberId.trim()) {
      setMemberVerifyError('Cafe24 아이디를 입력해주세요.')
      return
    }
    setIsVerifyingMember(true)
    setMemberVerifyError(null)
    if (!force) setNameMismatchWarning(null)
    try {
      const result = await seedingPublicApi.verifyCafe24Member(
        mallId, shopNo, campaignId, cafe24MemberId.trim(), force
      )
      if (!result.success && result.name_mismatch) {
        setNameMismatchWarning({
          customer_name: result.customer_name || '',
          applicant_name: result.applicant_name || '',
        })
        return
      }
      setNameMismatchWarning(null)
      const appData = await seedingPublicApi.getMyApplicationByCampaignPhone(campaignId)
      onApplicationUpdate(appData)
    } catch (err: any) {
      setMemberVerifyError(err.message || '검증에 실패했어요.')
    } finally {
      setIsVerifyingMember(false)
    }
  }

  // 쿠폰 발급 요청
  const handleRequestCoupon = async () => {
    setIsRequestingCoupon(true)
    setCouponRequestError(null)
    try {
      let variantCode = selectedCouponVariant
      const allowed = campaign?.allowed_variants
      if (!variantCode && allowed && allowed.length === 1) {
        variantCode = allowed[0].variant_code
      }
      await seedingPublicApi.requestCoupon(mallId, shopNo, campaignId, variantCode)
      const appData = await seedingPublicApi.getMyApplicationByCampaignPhone(campaignId)
      onApplicationUpdate(appData)
    } catch (err: any) {
      setCouponRequestError(err.message || '쿠폰 발급에 실패했어요.')
    } finally {
      setIsRequestingCoupon(false)
    }
  }

  return (
    <div className="pb-6 border-b border-border pt-6 overflow-hidden">
      <div className={`flex flex-col gap-4 ${forceMobile ? '' : 'md:grid md:grid-cols-[2fr_1fr] md:items-center md:gap-6'}`}>
        {/* 왼쪽: 상품 이미지 + 정보 */}
        <div className="flex items-start gap-4">
          {(campaign?.product_image_url || campaign?.thumbnail_url) && (
            <div className="w-32 h-32 flex-shrink-0 bg-surface-low rounded-lg overflow-hidden">
              <img
                src={campaign.product_image_url || campaign.thumbnail_url}
                alt={campaign.product_name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-base font-semibold text-on-surface">{campaign?.product_name}</h3>
            {campaign?.product_url && (
              <a
                href={campaign.product_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                {campaign?.shop_name || '쇼핑몰'}에서 상품 상세보기 &rarr;
              </a>
            )}
          </div>
        </div>

        {/* 오른쪽: 주문/배송 안내 영역 */}
        {previewMode ? (
          /* 미리보기: 배송 방식에 따른 간단 안내 */
          <div className="p-4 bg-surface rounded-lg">
            <p className="text-sm font-medium text-on-surface">
              {(campaign?.delivery_method || 'cafe24_order') === 'self_delivery'
                ? '자체 배송 캠페인'
                : '쇼핑몰 주문 캠페인'}
            </p>
            <p className="text-xs text-on-surface-variant mt-1">미리보기에서는 주문/배송 상태가 표시되지 않아요.</p>
          </div>
        ) : (campaign?.delivery_method || 'cafe24_order') === 'self_delivery' ? (
          /* 자체 배송 캠페인 — 송장 등록 전/후에 따라 다르게 표시 */
          <SelfDeliverySection orderStatus={orderStatus} />
        ) : !application.coupon_issued_at && campaign?.campaign_end_at && new Date(campaign.campaign_end_at) < new Date() ? (
          /* 캠페인 종료 */
          <div className="p-4 bg-surface-low rounded-lg text-center">
            <p className="text-sm text-on-surface-variant">캠페인이 종료되었어요.</p>
          </div>
        ) : !application.cafe24_member_id ? (
          /* cafe24_order + 아이디 미연동 → ID 입력 폼 */
          <Cafe24MemberForm
            campaign={campaign}
            cafe24MemberId={cafe24MemberId}
            onMemberIdChange={setCafe24MemberId}
            isVerifying={isVerifyingMember}
            onVerify={handleVerifyCafe24Member}
            verifyError={memberVerifyError}
            nameMismatchWarning={nameMismatchWarning}
          />
        ) : campaign?.auto_coupon && !application.coupon_issued_at ? (
          /* cafe24_order + 아이디 연동됨 + 쿠폰 미발급 → 옵션 선택 + 쿠폰 받기 */
          <CouponRequestForm
            campaign={campaign}
            application={application}
            selectedVariant={selectedCouponVariant}
            onSelectVariant={setSelectedCouponVariant}
            isRequesting={isRequestingCoupon}
            onRequest={handleRequestCoupon}
            requestError={couponRequestError}
          />
        ) : orderStatusLoading ? (
          <div className="p-4 bg-surface rounded-lg animate-pulse">
            <div className="h-4 bg-surface-high rounded w-1/2 mb-3" />
            <div className="h-3 bg-surface-high rounded w-3/4 mb-2" />
            <div className="h-3 bg-surface-high rounded w-2/3" />
          </div>
        ) : orderStatus?.has_order ? (
          /* 주문 확인됨 */
          <div className="p-4 bg-surface rounded-lg space-y-3">
            <p className="text-sm font-semibold text-on-surface">주문이 확인되었습니다</p>
            <div className="text-xs text-on-surface-variant space-y-1">
              {orderStatus.order_date && (
                <p>주문일: {formatDate(orderStatus.order_date)}</p>
              )}
              <p>배송 상태: <strong>{orderStatus.shipping_status_label}</strong></p>
            </div>
            <p className="text-xs text-on-surface-variant">상품 수령 후 미션을 진행해주세요!</p>
            <p className="text-xs text-outline-variant">
              {campaign?.shop_name || '쇼핑몰'} 아이디: {application.cafe24_member_id}
            </p>
          </div>
        ) : (
          /* 주문 대기 */
          <OrderWaitingSection
            campaign={campaign}
            application={application}
          />
        )}
      </div>
    </div>
  )
}

// ==================== 하위 컴포넌트 ====================

/**
 * 자체 배송(self_delivery) 안내 섹션
 *
 * 운영자가 송장(택배사 + 송장번호)을 등록하면 '발송 완료' + 배송조회를 노출하고,
 * 아직 등록 전이면 '배송 예정' 안내를 유지한다.
 */
function SelfDeliverySection({
  orderStatus,
}: {
  orderStatus: OrderStatusResponse | null
}) {
  const courier = orderStatus?.shipping_courier
  const invoice = orderStatus?.shipping_tracking_number
  const shipped = !!(courier && invoice)

  // 송장 미등록 — 배송 예정 안내 유지
  if (!shipped) {
    return (
      <div className="p-4 bg-primary/10 rounded-lg">
        <p className="text-sm font-medium text-primary mb-1">상품이 배송될 예정입니다</p>
        <p className="text-xs text-primary">
          신청 시 입력한 배송지로 배송됩니다.
          <br />
          상품 수령 후 미션을 진행해주세요!
        </p>
      </div>
    )
  }

  // 송장 등록됨 — 발송 완료 + 배송조회
  const trackingUrl = getTrackingUrl(courier, invoice)
  return (
    <div className="p-4 bg-primary/10 rounded-lg space-y-3">
      <p className="text-sm font-semibold text-primary">발송 완료</p>
      <div className="text-xs text-on-surface space-y-1">
        <p>
          택배사: <strong>{getCourierLabel(courier)}</strong>
        </p>
        {/* 송장번호는 복사하기 쉽도록 그대로 노출 (select-all) */}
        <p className="break-all">
          송장번호: <strong className="select-all">{invoice}</strong>
        </p>
      </div>
      {trackingUrl ? (
        <a
          href={trackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-full min-h-[44px] py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dim transition-colors"
        >
          배송조회 &rarr;
        </a>
      ) : (
        /* 기타(직접 조회) 등 조회 URL이 없는 택배사 안내 */
        <p className="text-xs text-on-surface-variant">
          택배사 홈페이지에서 위 송장번호로 직접 조회해주세요.
        </p>
      )}
      <p className="text-xs text-primary">상품 수령 후 미션을 진행해주세요!</p>
    </div>
  )
}

/** Cafe24 회원 ID 입력 폼 */
function Cafe24MemberForm({
  campaign,
  cafe24MemberId,
  onMemberIdChange,
  isVerifying,
  onVerify,
  verifyError,
  nameMismatchWarning,
}: {
  campaign: PublicCampaignResponse
  cafe24MemberId: string
  onMemberIdChange: (v: string) => void
  isVerifying: boolean
  onVerify: (force?: boolean) => void
  verifyError: string | null
  nameMismatchWarning: { customer_name: string; applicant_name: string } | null
}) {
  return (
    <div className="p-4 bg-surface rounded-lg space-y-3">
      <p className="text-sm font-semibold text-on-surface">쇼핑몰 계정 연동</p>
      <p className="text-xs text-on-surface-variant">
        {`${campaign?.shop_name || '쇼핑몰'} 아이디를 입력해주세요.`}
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          value={cafe24MemberId}
          onChange={(e) => onMemberIdChange(e.target.value)}
          placeholder={`${campaign?.shop_name || '쇼핑몰'} 로그인 아이디`}
          className="flex-1 min-w-0 px-3 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm bg-surface-lowest"
          onKeyDown={(e) => { if (e.key === 'Enter') onVerify() }}
        />
        <button
          onClick={() => onVerify()}
          disabled={isVerifying || !cafe24MemberId.trim()}
          className="px-4 py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dim disabled:opacity-50 whitespace-nowrap"
        >
          {isVerifying ? '확인중...' : '확인'}
        </button>
      </div>
      <p className="text-xs text-outline-variant">
        소셜로그인 사용 시 마이페이지 → 회원 정보 수정에서 아이디 확인
      </p>
      {verifyError && <p className="text-error text-xs">{verifyError}</p>}
      {nameMismatchWarning && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-800">
            쇼핑몰 회원 이름(<strong>{nameMismatchWarning.customer_name}</strong>)과
            신청자 이름(<strong>{nameMismatchWarning.applicant_name}</strong>)이 다릅니다.
          </p>
          <button
            onClick={() => onVerify(true)}
            disabled={isVerifying}
            className="mt-2 px-3 py-1.5 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            {isVerifying ? '처리중...' : '그래도 진행하기'}
          </button>
        </div>
      )}
      {campaign?.shop_domain && (
        <a
          href={`https://${campaign?.shop_domain}`}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full text-center py-2.5 border border-border rounded-lg text-sm text-on-surface hover:bg-surface-low transition-colors"
        >
          아직 회원이 아니신가요? <span className="font-medium text-primary">회원가입 →</span>
        </a>
      )}
    </div>
  )
}

/** 쿠폰 발급 요청 폼 */
function CouponRequestForm({
  campaign,
  application,
  selectedVariant,
  onSelectVariant,
  isRequesting,
  onRequest,
  requestError,
}: {
  campaign: PublicCampaignResponse
  application: MyApplicationResponse
  selectedVariant: string | null
  onSelectVariant: (v: string) => void
  isRequesting: boolean
  onRequest: () => void
  requestError: string | null
}) {
  const hasMultiplePrices = campaign.allowed_variants && new Set(campaign.allowed_variants.map(v => v.price)).size > 1

  return (
    <div className="p-4 bg-surface rounded-lg space-y-3">
      <p className="text-sm font-semibold text-on-surface">쿠폰 받기</p>
      {hasMultiplePrices ? (
        <>
          <p className="text-xs text-on-surface-variant">주문할 옵션을 선택한 후 쿠폰을 받아주세요.</p>
          <div className="space-y-2">
            {campaign.allowed_variants!.map((v) => (
              <label
                key={v.variant_code}
                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedVariant === v.variant_code
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="coupon_variant"
                    checked={selectedVariant === v.variant_code}
                    onChange={() => onSelectVariant(v.variant_code)}
                    className="w-4 h-4 text-primary"
                  />
                  <span className="text-sm text-on-surface">{v.label || v.variant_code}</span>
                </div>
              </label>
            ))}
          </div>
          {campaign.product_url && (
            <a
              href={campaign.product_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline"
            >
              옵션 상세정보 확인 →
            </a>
          )}
        </>
      ) : (
        <p className="text-xs text-on-surface-variant">아래 버튼을 눌러 구매 쿠폰을 받아주세요.</p>
      )}
      <button
        onClick={onRequest}
        disabled={isRequesting || !!(hasMultiplePrices && !selectedVariant)}
        className="w-full py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dim disabled:opacity-50 transition-colors"
      >
        {isRequesting ? '발급중...' : '쿠폰 받기'}
      </button>
      {requestError && <p className="text-error text-xs">{requestError}</p>}
      <p className="text-xs text-outline-variant">
        {campaign?.shop_name || '쇼핑몰'} 아이디: {application.cafe24_member_id}
      </p>
    </div>
  )
}

/** 주문 대기 섹션 */
function OrderWaitingSection({
  campaign,
  application,
}: {
  campaign: PublicCampaignResponse
  application: MyApplicationResponse
}) {
  return (
    <div className="p-4 bg-surface rounded-lg space-y-3">
      <p className="text-sm font-semibold text-on-surface">상품 주문</p>
      {campaign?.auto_coupon && application.coupon_issued_at ? (
        <div className="text-xs text-on-surface-variant space-y-1">
          <p>쿠폰이 발급되었어요. 쿠폰함에서 적용 후 주문해주세요.</p>
          <ul className="text-on-surface-variant space-y-0.5">
            {(() => {
              const vc = campaign.variant_coupons?.find(c =>
                application.selected_variant_code
                  ? c.variant_codes?.includes(application.selected_variant_code)
                  : true
              )
              return vc?.coupon_name
                ? <li>• {vc.coupon_name}</li>
                : campaign.product_coupon_name && <li>• {campaign.product_coupon_name}</li>
            })()}
            {campaign.shipping_coupon_name && <li>• {campaign.shipping_coupon_name}</li>}
          </ul>
          {application.selected_variant_code && campaign.allowed_variants && (
            <p className="text-outline-variant mt-1">
              선택 옵션: {(() => {
                const v = campaign.allowed_variants.find(v => v.variant_code === application.selected_variant_code)
                return v?.label || v?.variant_code || application.selected_variant_code
              })()}
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-on-surface-variant">상품을 주문해주세요.</p>
      )}
      {campaign?.product_url && (
        <a
          href={campaign.product_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full text-center py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dim transition-colors"
        >
          상품 주문하기 →
        </a>
      )}
      <p className="text-xs text-outline-variant">
        {campaign?.shop_name || '쇼핑몰'} 아이디: {application.cafe24_member_id}
      </p>
    </div>
  )
}
